import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useMemo, useCallback } from 'react'
import { StyledTable, CustomTablePagination } from './components'
import { ContractFilterHeader } from '../ContractFilterHeader'
import UserContractsTableHead from './components/UserContractsTableHead'
import UserContractsTableBody from './components/UserContractsTableBody'
import UserContractsTableBodySkeleton from './components/UserContractsTableBodySkeleton'

const UserContractsTable = React.memo(
  ({
    contracts,
    page,
    rowsPerPage,
    searchText,
    contractTypeFilter,
    contractStatusFilter,
    isLoading,
    onPageChange,
    onRowsPerPageChange,
    onSearchTextChanged,
    onContractTypeFilterChanged,
    onContractStatusFilterChanged,
    onResetFiltersPressed,
  }) => {
    const { t } = useTranslation()

    const handleChangePage = useCallback(
      (event, newPage) => {
        onPageChange(newPage)
      },
      [onPageChange]
    )

    const handleChangeRowsPerPage = useCallback(
      (event) => {
        const newRowsPerPage = parseInt(event.target.value, 10)
        onRowsPerPageChange(newRowsPerPage)
      },
      [onRowsPerPageChange]
    )

    // Transform the server-paginated grouped-by-users slice into the shape
    // the table body expects. We do not slice here — the server has already
    // applied skip/take.
    const userGroups = useMemo(() => {
      const groupedData = contracts?.data || []

      return groupedData.map((group) => {
        const fullName =
          `${group.firstName || ''} ${group.lastName || ''}`.trim() || '--'

        return {
          userId: group.endCustomerId || group.email,
          fullName,
          email: group.email,
          contractDataDto: group.contracts || [],
        }
      })
    }, [contracts?.data])

    // Total count for the pagination component. The backend may return a
    // `pagination` envelope (preferred). If it doesn't, we infer from the
    // current page: if we got a full page, there may be more (report -1 so
    // MUI shows "…of more than N" and keeps Next enabled); if we got a
    // partial page, this is the last one.
    const paginationData = useMemo(() => {
      const serverTotal = contracts?.pagination?.total
      if (typeof serverTotal === 'number') {
        return { totalCount: serverTotal }
      }
      const returned = contracts?.data?.length ?? 0
      if (returned < rowsPerPage) {
        return { totalCount: page * rowsPerPage + returned }
      }
      return { totalCount: -1 }
    }, [contracts?.pagination, contracts?.data, page, rowsPerPage])

    // Memoize filter header props
    const filterHeaderProps = useMemo(
      () => ({
        searchText,
        onSearchTextChanged,
        contractTypeFilter,
        onContractTypeFilterChanged,
        contractStatusFilter,
        onContractStatusFilterChanged,
        onResetFiltersPressed,
      }),
      [
        searchText,
        onSearchTextChanged,
        contractTypeFilter,
        onContractTypeFilterChanged,
        contractStatusFilter,
        onContractStatusFilterChanged,
        onResetFiltersPressed,
      ]
    )

    // Memoize table body props
    const tableBodyProps = useMemo(
      () => ({
        contracts,
        userGroups,
      }),
      [contracts, userGroups]
    )

    // Memoize pagination props
    const paginationProps = useMemo(
      () => ({
        rowsPerPageOptions: [5, 10, 25, 50],
        component: 'div',
        count: paginationData.totalCount,
        rowsPerPage,
        page,
        onPageChange: handleChangePage,
        onRowsPerPageChange: handleChangeRowsPerPage,
        labelRowsPerPage: t('LBLRowsPerPage'),
        showLastButton: true,
        showFirstButton: true,
        labelDisplayedRows: ({ from, to, count }) =>
          t('Pagination', { from, to, count }),
      }),
      [
        paginationData.totalCount,
        rowsPerPage,
        page,
        handleChangePage,
        handleChangeRowsPerPage,
        t,
      ]
    )

    // Show skeleton until the first server response arrives. `contracts` is
    // undefined before SWR has returned any data (including the period before
    // the request has even started, e.g. while the auth token is still
    // loading). Once data arrives we keep it on screen across filter/page
    // changes thanks to SWR's keepPreviousData.
    const showSkeleton = contracts == null || isLoading

    return (
      <>
        <ContractFilterHeader {...filterHeaderProps} />
        <StyledTable>
          <UserContractsTableHead />
          {showSkeleton && userGroups.length === 0 ? (
            <UserContractsTableBodySkeleton rowCount={rowsPerPage} />
          ) : (
            <UserContractsTableBody {...tableBodyProps} />
          )}
        </StyledTable>
        <CustomTablePagination {...paginationProps} />
      </>
    )
  }
)

UserContractsTable.displayName = 'UserContractsTable'

export default UserContractsTable
